<template>
  <section v-if="loading || hasData">
    <page-sub-header title="Find & Combine" :subtitle="isDataReady ? timeSeriesSubtitle : undefined" class="mt-4">
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <date-range-picker
            :from="dateFrom"
            :to="dateTo"
            :earliest="earliestAvailable"
            class="mr-3"
            @update:from="dateFrom = $event"
            @update:to="dateTo = $event"
          />
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
        </div>
      </template>
    </page-sub-header>

    <sme-card v-if="loading">
      <app-loading />
    </sme-card>

    <template v-else>
      <!-- Reach & engagement -->
      <chart-with-companion-kpis
        v-if="hasSignupsData"
        data-pdf-section="signups"
        card-class="mb-4"
        chart-title="Pension signups over time"
        chart-subtitle="Employees who confirmed the personal pension terms"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedSignups"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedSignups.name } } },
          }"
          fill-gradient
        />
        <template #companion>
          <app-stat
            :stat="totalSignups"
            :formatter="toInteger"
            description="Signups"
            class="mb-3"
            background-color="core-1-lighten-90"
            color="default"
            pdf-kpi
            delta-neutral
          />
          <app-stat
            :stat="signupRate"
            :formatter="toNumber"
            suffix="%"
            description="Signup rate"
            class="mb-3"
            background-color="core-1-lighten-90"
            color="default"
            pdf-kpi
            delta-neutral
          />
          <p class="mb-0">
            {{ timeGroupTitle }}, {{ toInteger(lastSignups) }} employees confirmed the personal pension terms.
            {{ signupsPrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>

      <!-- Found -->
      <chart-with-companion-kpis
        v-if="hasFoundData"
        data-pdf-section="found"
        card-class="mb-4"
        chart-title="Pension pots found over time"
        chart-subtitle="Pots found for members"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedPotsFound"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedPotsFound.name } } },
          }"
          fill-gradient
        />
        <template #companion>
          <app-stat
            :stat="totalPotsFound"
            :formatter="toInteger"
            description="Pots found"
            class="mb-3"
            background-color="core-5-lighten-90"
            color="default"
            pdf-kpi
            delta-neutral
          />
          <app-stat
            :stat="totalValueFound"
            :formatter="value => toInteger(Math.round(value))"
            :prefix="currencySymbol"
            description="Value found"
            class="mb-3"
            background-color="core-5-lighten-90"
            color="default"
            pdf-kpi
            delta-neutral
          />
          <p class="mb-0">{{ timeGroupTitle }}, {{ toInteger(lastPotsFound) }} pension pots were found.</p>
        </template>
      </chart-with-companion-kpis>

      <!-- Combined -->
      <chart-with-companion-kpis
        v-if="hasCombinedData"
        data-pdf-section="combined"
        card-class="mb-4"
        chart-title="Pensions combined over time"
        chart-subtitle="Consolidations completed"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedPotsCombined"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedPotsCombined.name } } },
          }"
          fill-gradient
        />
        <template #companion>
          <app-stat
            :stat="totalPotsCombined"
            :formatter="toInteger"
            description="Pots combined"
            class="mb-3"
            background-color="core-4-lighten-90"
            color="default"
            pdf-kpi
            delta-neutral
          />
          <app-stat
            :stat="totalValueCombined"
            :formatter="value => toInteger(Math.round(value))"
            :prefix="currencySymbol"
            description="Value combined"
            class="mb-3"
            background-color="core-4-lighten-90"
            color="default"
            pdf-kpi
            delta-neutral
          />
          <p class="mb-0">{{ timeGroupTitle }}, {{ toInteger(lastPotsCombined) }} consolidations completed.</p>
        </template>
      </chart-with-companion-kpis>
    </template>
  </section>
</template>

<script>
import { markerSection } from '@/utils/analyticsSections';

// Single source of truth for PensionMetrics' subsections, imported by both the
// Find & Combine tab's `sectionSpecs` and the homepage "Download all" capture.
export function pensionMetricsSections(pensionRef) {
  return [
    markerSection(pensionRef, {
      key: 'signups',
      label: 'Signups',
      hasDataKey: 'hasSignupsData',
      exportTableKey: 'getSignupsExportTable',
    }),
    markerSection(pensionRef, {
      key: 'found',
      label: 'Found',
      hasDataKey: 'hasFoundData',
      exportTableKey: 'getFoundExportTable',
    }),
    markerSection(pensionRef, {
      key: 'combined',
      label: 'Combined',
      hasDataKey: 'hasCombinedData',
      exportTableKey: 'getCombinedExportTable',
    }),
  ];
}
</script>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart from '@/components/charts/LineChart.vue';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import State from '@/state/State';
import useDateRangeSelection from '@/state/composables/useDateRangeSelection';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { TIME_SERIES_VALUES, getInitialAspectRatio } from '@/utils/chart';
import { toInteger, toNumber, currencySymbolFromCode } from '@/utils/common';

const { divisionId } = useDivisionSelection();
const { from: dateFrom, to: dateTo, apiFrom, apiTo } = useDateRangeSelection();

const loading = ref(true);
const earliestAvailable = ref(null);
const timeDataset = ref(null);
const enrolledDataset = ref(null);
const signupsDataset = ref(null);
const potsFoundDataset = ref(null);
const potsFoundValueDataset = ref(null);
const potsCombinedDataset = ref(null);
const potsCombinedValueDataset = ref(null);

const {
  timeGroupDataset,
  timeGroupTitle,
  timeSeries,
  timeSeriesSubtitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);

const currencySymbol = computed(() => currencySymbolFromCode(State.state.company.default_currency));

const timeGroupedSignups = computed(() => datasetToTimeGroupedDataset(signupsDataset));
const timeGroupedPotsFound = computed(() => datasetToTimeGroupedDataset(potsFoundDataset));
const timeGroupedPotsCombined = computed(() => datasetToTimeGroupedDataset(potsCombinedDataset));
const timeGroupedFoundValue = computed(() => datasetToTimeGroupedDataset(potsFoundValueDataset));
const timeGroupedCombinedValue = computed(() => datasetToTimeGroupedDataset(potsCombinedValueDataset));
const timeGroupedEnrolled = computed(() => datasetToTimeGroupedDataset(enrolledDataset));

const sumValues = dataset => (dataset?.values || []).reduce((total, value) => total + (value || 0), 0);
const lastValue = dataset => {
  const values = dataset?.values;
  return values?.length ? values[values.length - 1] : 0;
};

// KPIs are cumulative totals across the visible (complete) periods; the "last"
// helpers drive the per-period sentence beneath each chart.
const totalSignups = computed(() => sumValues(timeGroupedSignups.value));
const lastSignups = computed(() => lastValue(timeGroupedSignups.value));
const signupsPrevDiffText = computed(() => getPrevDiffText(timeGroupedSignups, toInteger));
const signupRate = computed(() => {
  const enrolled = lastValue(timeGroupedEnrolled.value);
  return enrolled ? Number(toNumber((totalSignups.value / enrolled) * 100)) : 0;
});

const totalPotsFound = computed(() => sumValues(timeGroupedPotsFound.value));
const lastPotsFound = computed(() => lastValue(timeGroupedPotsFound.value));
const totalValueFound = computed(() => sumValues(timeGroupedFoundValue.value));

const totalPotsCombined = computed(() => sumValues(timeGroupedPotsCombined.value));
const lastPotsCombined = computed(() => lastValue(timeGroupedPotsCombined.value));
const totalValueCombined = computed(() => sumValues(timeGroupedCombinedValue.value));

watch([divisionId, timeSeries, dateFrom, dateTo], () => getMetrics());

const getMetrics = async () => {
  loading.value = true;

  const { results, earliest_available } = await ApiClient.getPensionFindCombineMetrics(
    timeSeries.value,
    divisionId.value,
    apiFrom.value,
    apiTo.value,
  );

  earliestAvailable.value = earliest_available ?? null;

  if (results?.length) {
    const byId = id => results.find(result => result.id === id);

    // A series missing from `results` must stay null so the time-group helpers
    // skip it; spreading `undefined` yields a `values`-less object that throws
    // when datasetToTimeGroupedDataset slices it.
    const namedSeries = (id, name, lineColor) => {
      const series = byId(id);
      return series ? { ...series, name, lineColor } : null;
    };

    timeDataset.value = byId('time');
    enrolledDataset.value = byId('enrolled');
    signupsDataset.value = namedSeries('signups', 'Signups', PaletteColors['core-1']);
    potsFoundDataset.value = namedSeries('pots_found', 'Pots found', PaletteColors['core-5']);
    potsFoundValueDataset.value = byId('pots_found_value');
    potsCombinedDataset.value = namedSeries('pots_combined', 'Pots combined', PaletteColors['core-4']);
    potsCombinedValueDataset.value = byId('pots_combined_value');
  }

  loading.value = false;
};

onBeforeMount(() => getMetrics());

const hasSignupsData = computed(() => timeGroupedSignups.value?.values?.length > 0);
const hasFoundData = computed(() => timeGroupedPotsFound.value?.values?.length > 0);
const hasCombinedData = computed(() => timeGroupedPotsCombined.value?.values?.length > 0);

const hasData = computed(() => !loading.value && (hasSignupsData.value || hasFoundData.value || hasCombinedData.value));
const isDataReady = computed(() => hasData.value && !loading.value);

const exportRow = index => ({
  time: timeGroupDataset.value.values[index],
  signups: timeGroupedSignups.value?.values[index],
  potsFound: timeGroupedPotsFound.value?.values[index],
  valueFound: timeGroupedFoundValue.value?.values[index],
  potsCombined: timeGroupedPotsCombined.value?.values[index],
  valueCombined: timeGroupedCombinedValue.value?.values[index],
});

function getSignupsExportTable() {
  if (!hasSignupsData.value) {
    return null;
  }
  return {
    headers: ['Date', 'Signups'],
    rows: timeGroupDataset.value.values.map((time, index) => [time, exportRow(index).signups]),
  };
}

function getFoundExportTable() {
  if (!hasFoundData.value) {
    return null;
  }
  return {
    headers: ['Date', 'Pots found', 'Value found'],
    rows: timeGroupDataset.value.values.map((time, index) => {
      const row = exportRow(index);
      return [time, row.potsFound, Math.round(row.valueFound || 0)];
    }),
  };
}

function getCombinedExportTable() {
  if (!hasCombinedData.value) {
    return null;
  }
  return {
    headers: ['Date', 'Pots combined', 'Value combined'],
    rows: timeGroupDataset.value.values.map((time, index) => {
      const row = exportRow(index);
      return [time, row.potsCombined, Math.round(row.valueCombined || 0)];
    }),
  };
}

defineExpose({
  hasData,
  hasSignupsData,
  hasFoundData,
  hasCombinedData,
  getSignupsExportTable,
  getFoundExportTable,
  getCombinedExportTable,
});
</script>
